import { parseStreamList } from 'src/services/LeadStreamsService/streamList';
import { getStreamDisplayName, getStreamStatus, hasActiveStreamAccess } from 'src/utils/leadStreams';

import type { Stream } from 'src/services/LeadStreamsService/agent';

export type StreamCommandIntent = { active: boolean; target: string };
export type StreamCommandTarget = { id: number; active: boolean };

const normalize = (text: string) =>
    text
        .toLowerCase()
        .replace(/ё/g, 'е')
        .replace(/[«»"“”]/g, '')
        .trim();

// Only explicit commands enter this path. Model output and saved chat never execute it.
export function parseStreamCommand(text: string): StreamCommandIntent | null {
    const q = normalize(text)
        .replace(/^пожалуйста[, ]+/, '')
        .replace(/[.!]+$/, '')
        .trim();
    const pause =
        q.match(/^(?:поставь(?:те)?|поставить) (.+) на паузу$/) ||
        q.match(/^(?:поставь(?:те)?|поставить) на паузу (.+)$/) ||
        q.match(/^(?:приостанови(?:те)?|приостановить|останови(?:те)?|выключи(?:те)?) (.+)$/);
    const resume = q.match(/^(?:возобнови(?:те)?|возобновить|включи(?:те)?) (.+)$/);
    const match = pause || resume;
    if (!match) return null;
    const target = match[1];
    if (/[?;\n]|\bhttps?:|(?:^| )(?:не|а|и|затем|потом) (?:включ|выключ|останов|возобнов|удал|оплат)/.test(target))
        return null;
    return { active: !pause, target };
}

export function commandCandidates(intent: StreamCommandIntent, streams: Stream[], currentId?: number | null) {
    const target = normalize(intent.target);
    if (/^(?:(?:этот|текущий|мой) )?поток(?: лидов)?$/.test(target)) {
        return currentId ? streams.filter((s) => s.id === currentId) : streams;
    }
    if (/^(?:все|мои) потоки(?: лидов)?$/.test(target)) return streams;
    const name = target.replace(/^поток(?: лидов)?\s*/, '').replace(/^[№#]\s*/, '');
    if (/^[1-9]\d*$/.test(name)) return streams.filter((s) => s.id === Number(name));
    return streams.filter((s) => normalize(getStreamDisplayName(s)) === name);
}

export function readCommandStream(data: unknown, id: number, userId?: number): Stream {
    const stream = parseStreamList([data])[0];
    if (stream.id !== id || (userId !== undefined && stream.user !== userId))
        throw new Error('Сервер вернул другой поток.');
    return stream;
}

export function commandUnavailable(stream: Stream, active: boolean): string | null {
    if (!active) return null;
    if (stream.category.is_active === false || stream.category.is_delivery_enabled === false)
        return 'Категория сейчас отключена. Возобновление недоступно.';
    if (!(stream.balance > 0 || hasActiveStreamAccess(stream)))
        return 'Для возобновления нужен действующий пакет или доступ. Откройте поток и выберите объём.';
    return null;
}

export function commandStateMatches(stream: Stream, active: boolean) {
    if (stream.is_active !== active) return false;
    if (!active) return !stream.status || stream.status !== 'active';
    return !commandUnavailable(stream, true) && getStreamStatus(stream).key === 'active';
}

export function commandResult(stream: Stream, changed: boolean) {
    const time = new Date().toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
    const result = stream.is_active ? 'Поиск включён.' : 'Поиск на паузе.';
    return `«${getStreamDisplayName(stream)}» · поток №${stream.id}\n${result}\n${
        changed ? 'Изменение подтверждено' : 'Состояние проверено'
    } в ${time}.`;
}
